import { createAsyncThunk } from '@reduxjs/toolkit'
import { saveAs } from 'file-saver'
import type { RootStateType } from 'store'
import { callErrorMsg } from 'helpers/errorMsg'
import { toastSuccess } from 'utils/toast'
import { getDocFileUrlAPI } from 'features/SCMS/services/configBank'
import {
  fetchScmsCampaignListApi,
  fetchCampaignWidgetSummaryApi,
  submitCampaignCorrectionsApi,
  fetchCampaignHistoryApi,
  fetchCampaignNeedPlanCountApi,
  exportCampaignApi,
} from '../../services/campaign'
import type {
  CampaignCorrectionPayloadType,
  CampaignCorrectionResultType,
  CampaignFilterParamsType,
  CampaignHistoryParamsType,
} from '../../@types/campaignTypes'

const sliceName = 'scmsCampaign'

export const fetchScmsCampaignList = createAsyncThunk(
  `${sliceName}/fetchScmsCampaignList`,
  async (_, { getState, rejectWithValue }) => {
    try {
      const { appliedCampaignFilters } = (getState() as RootStateType).scmsCampaign
      const [listResponse, widgetResponse, needPlanResponse] = await Promise.all([
        fetchScmsCampaignListApi(appliedCampaignFilters),
        fetchCampaignWidgetSummaryApi(appliedCampaignFilters),
        fetchCampaignNeedPlanCountApi(appliedCampaignFilters),
      ])
      return {
        list: listResponse.data,
        widget: widgetResponse.data,
        needPlan: needPlanResponse.data,
      }
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const fetchScmsCampaignHistoryList = createAsyncThunk(
  `${sliceName}/fetchScmsCampaignHistoryList`,
  async (params: CampaignHistoryParamsType, { rejectWithValue }) => {
    try {
      const response = await fetchCampaignHistoryApi(params)
      return response.data
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const submitCampaignCorrections = createAsyncThunk(
  `${sliceName}/submitCampaignCorrections`,
  async (corrections: CampaignCorrectionPayloadType[], { rejectWithValue }) => {
    try {
      const result: CampaignCorrectionResultType = { successRows: [], failedRows: [] }
      for (let index = 0; index < corrections.length; index += 50) {
        // Process subsequent batches even when an earlier batch has row-level failures.
        // eslint-disable-next-line no-await-in-loop
        const response = await submitCampaignCorrectionsApi(corrections.slice(index, index + 50))
        const { data } = response.data
        result.successRows.push(...data.success_rows.map((row) => row.row_id))
        result.failedRows.push(...data.failed_rows)
      }
      return result
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)

export const downloadCampaignCsv = createAsyncThunk(
  `${sliceName}/downloadCampaignCsv`,
  async (_, { getState, rejectWithValue }) => {
    try {
      const { appliedCampaignFilters } = (getState() as RootStateType).scmsCampaign
      const filters: Partial<CampaignFilterParamsType> = { ...appliedCampaignFilters }
      delete filters.cursor
      delete filters.page_size
      delete filters.sort_by
      delete filters.sort_direction
      const response = await exportCampaignApi(filters)
      const exportData = response.data

      if (exportData?.queuing) {
        toastSuccess('File is being queued for download. It will send to your email shortly.')
      } else if (exportData?.file_url) {
        const downloadResponse = await getDocFileUrlAPI(exportData.file_url, 'application/csv')
        saveAs(downloadResponse?.data, 'scms_campaign_export.csv')
        toastSuccess('Export campaign file success')
      }
      return exportData
    } catch (e) {
      callErrorMsg(e)
      return rejectWithValue((e as Error).message)
    }
  },
)
